<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
// Пример локального импорта, если у вас иконки лежат в папке assets/icons
// import BaseballIcon from '@/assets/icons/baseball.png'
// import SoccerIcon   from '@/assets/icons/soccer.png'
// import IceHockeyIcon from '@/assets/icons/icehockey.png'
// import RugbyIcon    from '@/assets/icons/rugby.png'

const props = defineProps({
  availableLeagues: {
    type: Array,
    required: true
  }
});
const emit = defineEmits(['updateFilter']);

// 1) Маппинг «sport.group → URL или путь к иконке»
const sportIconMap = {
  soccer:   'https://img.icons8.com/?size=100&id=61032&format=png&color=ffffff',       // Football (Soccer)
  basketball: 'https://img.icons8.com/?size=100&id=fCa2W359XPO3&format=png&color=ffffff', // NBA
  icehockey: 'https://img.icons8.com/?size=100&id=pg0oNK0iG0gx&format=png&color=ffffff',  // NHL
  rubgyleague: 'https://img.icons8.com/?size=100&id=9775&format=png&color=ffffff',       // Rugby League
  rugbyunion: 'https://img.icons8.com/?size=100&id=9775&format=png&color=ffffff',        // Rugby Union (можно поменять URL)
  esports:    'https://img.icons8.com/?size=100&id=0zCSPyEKiaTL&format=png&color=ffffff', // CSGO-значок (или общий eSports)
  americanfootball: 'https://img.icons8.com/?size=100&id=9775&format=png&color=ffffff', // NFL/UFL
  baseball:  'https://img.icons8.com/?size=100&id=74475&format=png&color=ffffff',        // MLB
  cricket:   'https://img.icons8.com/?size=100&id=9836&format=png&color=ffffff',
  cricket_t20_blast:'https://img.icons8.com/?size=100&id=9836&format=png&color=ffffff',
 rugbyleague : 'https://img.icons8.com/?size=100&id=9775&format=png&color=ffffff' // Rugby League
    // Cricket
  // …добавьте другие виды спорта, если нужно…
};

// 2) Функция, чтобы получить URL иконки по ключу sport.group
function getSportIconUrl(groupKey) {
  
  // Если в маппе нет ключа, вернём null → тогда картинка просто не отобразится
  return sportIconMap[groupKey] || null;
}

// Состояния выбора вида спорта и лиги
const selectedSport = ref(null);
const selectedLeague = ref(null);
const leaguesExpanded = ref(false);

// Функция для «красивого» отображения названия спорт-группы
function beautifySportGroup(groupKey) {
  switch (groupKey) {
    case 'soccer':      return 'Soccer';
    case 'basketball':  return 'Basketball';
    case 'esports':     return 'Esports';
    case 'icehockey':   return 'Ice Hockey';
    case 'rugbyleague': return 'Rugby League';
    case 'rugbyunion':  return 'Rugby Union';
    case 'americanfootball': return 'football';

    case 'baseball':    return 'Baseball';



    default:
      return groupKey[0].toUpperCase() + groupKey.slice(1);
  }
}

// Список «Sport» без дублей по полю group
const sportsList = computed(() => {
  const seen = new Set();
  const arr = [];

  props.availableLeagues.forEach((league) => {
    if (!seen.has(league.group)) {
      seen.add(league.group);
      arr.push({
        value: league.group,
        label: beautifySportGroup(league.group)
      });
    }
  });

  return arr;
});

// Список «League» для выбранного Sport (фильтрация по exact match group)
const leaguesForSelectedSport = computed(() => {
  if (!selectedSport.value) return [];
  return props.availableLeagues.filter(
    league => league.group === selectedSport.value
  );
});

// Эмитим наружу при изменении фильтра
watch([selectedSport, selectedLeague], () => {
  emit('updateFilter', {
    sport: selectedSport.value,
    league: selectedLeague.value
  });
});

function selectSport(sport) {
  selectedSport.value = sport;
  selectedLeague.value = null;   
  leaguesExpanded.value = false; 
}

function selectLeague(leagueKey) {
  selectedLeague.value = leagueKey;
}

function clearFilters() {
  selectedSport.value = null;
  selectedLeague.value = null;
  leaguesExpanded.value = false;
}
</script>

<template>
  <div class="bg-gray-800 rounded-lg p-4 shadow-lg">
    <div class="space-y-4">
      <!-- Секция выбора вида спорта -->
      <div>
        <h3 class="text-lg font-semibold mb-2">Sport</h3>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="sport in sportsList"
            :key="sport.value"
            @click="selectSport(sport.value)"
            :class="[
              'px-3 py-1 rounded-full text-sm transition-colors duration-200 capitalize flex items-center gap-1',
              selectedSport === sport.value
                ? 'bg-blue-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            ]"
          >
            <!-- 3) Вставляем иконку: если URL есть, показываем <img> перед лейблом -->
            <img
              v-if="getSportIconUrl(sport.value)"
              :src="getSportIconUrl(sport.value)"
              alt="Icon"
              class="w-4 h-4 object-contain"
            />
            <span>{{ sport.label }}</span>
          </button>
        </div>
      </div>

      <!-- Секция выбора лиги (появляется только если выбран Sport) -->
      <div v-if="selectedSport">
        <h3 class="text-lg font-semibold mb-2">League</h3>
        <div
          :class="[
            'transition-all duration-300 ease-in-out overflow-hidden',
            leaguesExpanded ? 'max-h-full' : 'max-h-40'
          ]"
        >
          <div class="flex flex-wrap gap-2">
            <button
              v-for="league in leaguesForSelectedSport"
              :key="league.key"
              @click="selectLeague(league.key)"
              :class="[
                'px-3 py-1 rounded-full text-sm transition-colors duration-200 flex items-center gap-1',
                selectedLeague === league.key
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              ]"
            >
              <!-- По аналогии можем добавить иконку для каждой лиги, если надо -->
              <span>{{ league.title }}</span>
            </button>
          </div>
        </div>

        <!-- Кнопка «Show All / Hide» -->
        <div class="mt-2 text-center">
          <button
            @click="leaguesExpanded = !leaguesExpanded"
            class="text-sm text-blue-400 hover:underline focus:outline-none"
          >
            {{ leaguesExpanded ? 'Hide' : 'Show All' }}
          </button>
        </div>
      </div>

      <!-- Кнопка сброса фильтров -->
      <button
        v-if="selectedSport || selectedLeague"
        @click="clearFilters"
        class="w-full bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition-colors duration-200"
      >
        Clear Filters
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Ваши стили, если нужны дополнительные правки */
</style>
